import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Loader2 } from "lucide-react";

const TYPES = [
  ["spot_paaslag", "Spot + påslag"], ["spot", "Spot"], ["fastpris", "Fastpris"],
  ["variabel", "Variabel"], ["norgespris", "Norgespris"], ["bedrift", "Bedrift"],
];
const input = "w-full h-9 px-3 rounded-md bg-zinc-950 border border-zinc-800 text-sm text-zinc-200";
const today = () => new Date().toISOString().slice(0, 10);

function L({ label, children }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1">{label}</label>
      {children}
    </div>
  );
}

export const NyAvtaleButton = ({ customerId, onCreated }) => {
  const [open, setOpen] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [meters, setMeters] = useState([]);
  const [f, setF] = useState({ customer_id: customerId || "", metering_point_id: "", type: "spot_paaslag", start_date: today(), markup_ore_per_kwh: "", monthly_fee_nok: "", fixed_price_ore: "", binding_months: "0" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  useEffect(() => {
    if (open && !customerId) {
      api.get("/customers?limit=200").then(({ data }) => setCustomers(data.items || []));
    }
  }, [open, customerId]);
  useEffect(() => {
    if (f.customer_id) {
      api.get(`/metering-points?customer_id=${f.customer_id}`).then(({ data }) => setMeters(data.items || data || []));
    }
  }, [f.customer_id]);

  const save = async () => {
    setBusy(true);
    try {
      const num = (v) => (v === "" ? undefined : Number(v));
      await api.post("/contracts", {
        customer_id: f.customer_id, metering_point_id: f.metering_point_id || undefined, type: f.type,
        start_date: new Date(f.start_date).toISOString(), markup_ore_per_kwh: num(f.markup_ore_per_kwh) || 0,
        monthly_fee_nok: num(f.monthly_fee_nok) || 0, fixed_price_ore: num(f.fixed_price_ore), binding_months: num(f.binding_months) || 0,
      });
      toast.success("Avtale opprettet");
      setOpen(false);
      onCreated && onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || "Kunne ikke opprette avtale"); }
    finally { setBusy(false); }
  };

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="ny-avtale-btn"
        className="h-9 px-3 rounded-md bg-emerald-500 hover:bg-emerald-400 text-black text-sm font-medium flex items-center gap-1.5 transition-colors">
        <Plus className="h-4 w-4" /> Ny avtale
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0a0a0a] border-zinc-900" data-testid="ny-avtale-dialog">
          <DialogHeader><DialogTitle className="text-zinc-100">Ny strømavtale</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            {!customerId && (
              <div className="col-span-2">
                <L label="Kunde">
                  <select value={f.customer_id} onChange={set("customer_id")} className={input} data-testid="avtale-customer">
                    <option value="">Velg kunde</option>
                    {customers.map((c) => <option key={c.id} value={c.id}>{c.full_name} · {c.customer_number}</option>)}
                  </select>
                </L>
                {customers.length === 0 && <div className="text-xs text-amber-400 mt-1" data-testid="avtale-no-customers">Ingen kunder ennå – opprett en kunde under Kunder først.</div>}
              </div>
            )}
            <div className="col-span-2">
              <L label="Målepunkt (valgfritt)">
                <select value={f.metering_point_id} onChange={set("metering_point_id")} className={input} data-testid="avtale-meter">
                  <option value="">Ingen / legg til senere</option>
                  {meters.map((m) => <option key={m.id} value={m.id}>{m.mpid} · {m.address}</option>)}
                </select>
              </L>
            </div>
            <L label="Avtaletype">
              <select value={f.type} onChange={set("type")} className={input} data-testid="avtale-type">
                {TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </L>
            <L label="Startdato"><input type="date" value={f.start_date} onChange={set("start_date")} className={input} data-testid="avtale-start" /></L>
            {f.type === "fastpris"
              ? <L label="Fastpris (øre/kWh)"><input type="number" value={f.fixed_price_ore} onChange={set("fixed_price_ore")} className={input} data-testid="avtale-fixed" /></L>
              : <L label="Påslag (øre/kWh)"><input type="number" value={f.markup_ore_per_kwh} onChange={set("markup_ore_per_kwh")} className={input} data-testid="avtale-markup" /></L>}
            <L label="Månedsbeløp (kr)"><input type="number" value={f.monthly_fee_nok} onChange={set("monthly_fee_nok")} className={input} data-testid="avtale-fee" /></L>
            <L label="Binding (måneder)"><input type="number" value={f.binding_months} onChange={set("binding_months")} className={input} data-testid="avtale-binding" /></L>
          </div>
          <button onClick={save} disabled={busy || !f.customer_id} data-testid="avtale-save"
            className="mt-2 h-10 rounded-md bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 text-black text-sm font-medium flex items-center justify-center gap-2">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Opprett avtale
          </button>
        </DialogContent>
      </Dialog>
    </>
  );
};
